<template>
  <div class="ai-search-page" :class="{ desktop: $isDesktop }">
    <!-- Hero Section with outdoor texture -->
    <section class="ai-hero">
      <!-- Topographic pattern overlay -->
      <div class="hero-pattern" aria-hidden="true">
        <svg
          class="topo-lines"
          viewBox="0 0 400 200"
          preserveAspectRatio="xMidYMid slice"
        >
          <path
            d="M0,100 Q100,80 200,100 T400,100"
            fill="none"
            stroke="rgba(255,255,255,0.08)"
            stroke-width="1"
          />
          <path
            d="M0,120 Q100,100 200,120 T400,120"
            fill="none"
            stroke="rgba(255,255,255,0.06)"
            stroke-width="1"
          />
          <path
            d="M0,140 Q100,120 200,140 T400,140"
            fill="none"
            stroke="rgba(255,255,255,0.04)"
            stroke-width="1"
          />
          <path
            d="M0,80 Q100,60 200,80 T400,80"
            fill="none"
            stroke="rgba(255,255,255,0.05)"
            stroke-width="1"
          />
          <path
            d="M0,60 Q100,40 200,60 T400,60"
            fill="none"
            stroke="rgba(255,255,255,0.03)"
            stroke-width="1"
          />
        </svg>
      </div>

      <div class="hero-content">
        <h1 class="ai-title">
          {{ $t('Find your') }}
          <span class="title-highlight">{{ $t('perfect campsite') }}</span>
        </h1>
        <p class="ai-subtitle">
          {{ $t("Just describe what you're looking for") }}
        </p>

        <div class="search-card">
          <form class="search-form" @submit.prevent="handleSearch">
            <div class="input-wrapper">
              <div class="input-icon">
                <img
                  src="@/assets/images/magnify-blue.svg"
                  :alt="$t('Search')"
                  width="26"
                  height="26"
                />
              </div>
              <input
                ref="searchInput"
                v-model="query"
                type="text"
                class="ai-search-input"
                :class="{ 'with-action-btn': isSpeechSupported || query }"
                :placeholder="currentPlaceholder"
                :disabled="isLoading"
                maxlength="500"
                autocomplete="off"
                data-test="ai-search-input"
              />
              <button
                v-if="isSpeechSupported && !query"
                type="button"
                class="mic-btn"
                :class="{ 'is-listening': isListening }"
                :aria-label="
                  isListening ? $t('Stop listening') : $t('Search by voice')
                "
                :disabled="isLoading"
                @click="handleMicClick"
              >
                <span v-if="isListening" class="mic-pulse"></span>
                <svg viewBox="0 0 24 24" fill="currentColor" class="mic-icon">
                  <path
                    d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z"
                  />
                  <path
                    d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z"
                  />
                </svg>
              </button>
              <button
                v-if="query"
                type="button"
                class="clear-btn"
                :aria-label="$t('Clear search')"
                @click="clearInput"
              >
                <svg viewBox="0 0 20 20" fill="currentColor">
                  <path
                    fill-rule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clip-rule="evenodd"
                  />
                </svg>
              </button>
            </div>

            <button
              type="submit"
              class="button primary"
              :class="{ loading: isLoading }"
              :disabled="isLoading || !query.trim()"
              data-test="ai-search-button"
            >
              <span class="btn-text">{{ $t('Find campsites') }}</span>
              <loading-spinner
                v-if="isLoading"
                stroke-color="white"
                location="ai-search-btn"
                class="btn-spinner"
              />
            </button>
          </form>

          <!-- Error Display -->
          <transition name="error-slide">
            <div
              v-if="errorMessage"
              class="error-banner"
              role="alert"
              data-test="ai-search-error"
            >
              <svg class="error-icon" viewBox="0 0 20 20" fill="currentColor">
                <path
                  fill-rule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                  clip-rule="evenodd"
                />
              </svg>
              <p>{{ errorMessage }}</p>
              <button
                class="dismiss-btn"
                :aria-label="$t('Dismiss')"
                @click="errorMessage = null"
              >
                <svg viewBox="0 0 20 20" fill="currentColor">
                  <path
                    fill-rule="evenodd"
                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                    clip-rule="evenodd"
                  />
                </svg>
              </button>
            </div>
          </transition>
        </div>
      </div>
    </section>

    <!-- Tips for better results -->
    <section class="tips-section">
      <div class="section-header">
        <h2>{{ $t('For best results, mention:') }}</h2>
      </div>
      <div class="tips-grid">
        <div class="tip-item">
          <span class="tip-emoji">
            <img
              class="family icon"
              src="@/assets/images/family.svg"
              alt=""
              width="25"
              height="20"
          /></span>
          <span class="tip-text"
            ><strong>{{ $t('Who') }}</strong> –
            {{ $t('2 adults, kids aged 5 and 8') }}</span
          >
        </div>
        <div class="tip-item">
          <span class="tip-emoji"
            ><img
              class="dates icon"
              src="@/assets/images/calendar.svg"
              alt=""
              width="22"
              height="22"
          /></span>
          <span class="tip-text"
            ><strong>{{ $t('When') }}</strong> –
            {{ $t('next weekend, 3 nights') }}</span
          >
        </div>
        <div class="tip-item">
          <span class="tip-emoji"
            ><img
              src="~/assets/images/location-map.svg"
              alt=""
              width="22"
              height="21"
          /></span>
          <span class="tip-text"
            ><strong>{{ $t('Where') }}</strong> –
            {{ $t('Lake District, Cornwall') }}</span
          >
        </div>
        <div class="tip-item">
          <span class="tip-emoji">
            <img
              src="~/assets/images/category-icons/rent-a-tent.svg"
              alt=""
              width="24"
              height="19"
            />
          </span>
          <span class="tip-text"
            ><strong>{{ $t('Style') }}</strong> –
            {{ $t('glamping, cabin, tent') }}</span
          >
        </div>
        <div class="tip-item">
          <span class="tip-emoji">
            <img
              src="~/assets/images/dog-green.svg"
              alt=""
              width="23"
              height="21"
            />
          </span>
          <span class="tip-text"
            ><strong>{{ $t('Features') }}</strong> –
            {{ $t('hot tub, dog-friendly') }}</span
          >
        </div>
      </div>
    </section>

    <!-- Example Searches as adventure cards -->
    <section class="examples-section">
      <div class="section-header">
        <h2>{{ $t('Not sure where to start?') }}</h2>
      </div>

      <div
        class="examples-grid"
        :class="{ 'is-transitioning': isExamplesTransitioning }"
      >
        <button
          v-for="(example, idx) in activeExamples"
          :key="`${example}-${idx}`"
          class="example-card"
          :style="{ '--delay': `${idx * 40}ms` }"
          :disabled="isLoading"
          @click="searchWithExample(example)"
        >
          <span class="example-text">{{ example }}</span>
          <span class="example-arrow">→</span>
        </button>
      </div>
    </section>

    <!-- How it works - visual storytelling -->
    <section class="how-section">
      <h2>{{ $t('How it works') }}</h2>

      <div class="steps-flow">
        <div class="step-card">
          <div class="step-visual write">
            <svg viewBox="0 0 24 24" fill="none">
              <path
                d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>
          <h3>{{ $t('Describe your trip') }}</h3>
          <p>{{ $t("Just type what you're looking for") }}</p>
        </div>

        <div class="step-connector">
          <svg viewBox="0 0 24 24" fill="none">
            <path
              d="M5 12h14M12 5l7 7-7 7"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </div>

        <div class="step-card">
          <div class="step-visual think">
            <svg viewBox="0 0 24 24" fill="none">
              <circle
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                stroke-width="2"
              />
              <path
                d="M12 16v.01M12 12a2 2 0 10-2-2"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
              />
            </svg>
            <div class="think-dots">
              <span />
              <span />
              <span />
            </div>
          </div>
          <h3>{{ $t('We understand') }}</h3>
          <p>{{ $t('We figure out dates, places, and what you need') }}</p>
        </div>

        <div class="step-connector">
          <svg viewBox="0 0 24 24" fill="none">
            <path
              d="M5 12h14M12 5l7 7-7 7"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </div>

        <div class="step-card">
          <div class="step-visual success">
            <svg viewBox="0 0 24 24" fill="none">
              <path
                d="M22 11.08V12a10 10 0 11-5.93-9.14"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M22 4L12 14.01l-3-3"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>
          <h3>{{ $t('See matches') }}</h3>
          <p>{{ $t('Go straight to campsites that fit') }}</p>
        </div>
      </div>
    </section>

    <!-- Loading Overlay -->
    <ai-search-loading v-if="isLoading" :query="query" />

    <!-- Confirmation Dialog -->
    <ai-search-confirm-dialog
      v-if="pendingSearchData"
      :show="showConfirmDialog"
      :search-data="pendingSearchData"
      @confirm="handleConfirm"
      @reject="handleReject"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useSpeechRecognition } from '~/composables/useSpeechRecognition'
import type { AiSearchConfirmData } from '~/components/ai-search/AiSearchConfirmDialog.vue'
import { getCampsiteCategoriesPresenter } from '~/apps/categories/presenter-json/CampsiteCategoriesUpdaterJson'
import { useFacetsStore } from '~/stores/useFacetsStore'
import { getFeedback, setContext } from '@sentry/nuxt'
import { isGlamping } from '~/utility/glamping'

definePageMeta({
  layout: 'default',
})

// Set cache headers on server - this is a static page that can be cached
if (import.meta.server) {
  const event = useRequestEvent()
  const $config = useRuntimeConfig()
  // Use longer cache time since this is a static landing page (5 minutes)
  const cacheTime = 300
  event!.node.res.setHeader('Cache-Control', `max-age=${cacheTime}, public`)
  event!.node.res.setHeader('Grace', `${$config.nonPriceGraceTime}`)
  event!.node.res.setHeader('xkey', 'page:ai-search')
}

const runtimeConfig = useRuntimeConfig()
const { $isDesktop, $i18n } = useNuxtApp()
const route = useRoute()

const langCode = computed(() => (route.params.lang as string) || 'en-gb')
const { t } = useI18n()

// SEO meta tags
const pageTitle = computed(
  () => ($i18n.$t('Find Your Perfect Campsite') as string) + ' | Pitchup.com',
)
const pageDescription = computed(
  () =>
    $i18n.$t(
      "Describe your ideal camping trip in your own words - who's going, when, where, and what matters to you. We'll find campsites that match.",
    ) as string,
)
const canonicalUrl = computed(
  () => `https://${runtimeConfig.public.envUrl}${route.path}`,
)

const headData = computed(() => ({
  title: pageTitle.value,
  link: [{ rel: 'canonical', href: canonicalUrl.value }],
  meta: [
    {
      hid: 'description',
      name: 'description',
      content: pageDescription.value,
    },
    // Open Graph
    {
      hid: 'og:title',
      property: 'og:title',
      content: pageTitle.value,
    },
    {
      hid: 'og:description',
      property: 'og:description',
      content: pageDescription.value,
    },
    {
      hid: 'og:url',
      property: 'og:url',
      content: canonicalUrl.value,
    },
    { hid: 'og:type', property: 'og:type', content: 'website' },
    {
      hid: 'og:image',
      property: 'og:image',
      content: `https://${runtimeConfig.public.envUrl}/app-icons/pitchup_logo_large.png`,
    },
    // Twitter Card
    { hid: 'twitter:card', name: 'twitter:card', content: 'summary' },
    {
      hid: 'twitter:title',
      name: 'twitter:title',
      content: pageTitle.value,
    },
    {
      hid: 'twitter:description',
      name: 'twitter:description',
      content: pageDescription.value,
    },
    {
      hid: 'twitter:image',
      name: 'twitter:image',
      content: `https://${runtimeConfig.public.envUrl}/app-icons/pitchup_logo_large.png`,
    },
  ],
  script: [
    {
      type: 'application/ld+json',
      innerHTML: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: $i18n.$t('Find Your Perfect Campsite') as string,
        description: pageDescription.value,
        url: canonicalUrl.value,
        publisher: {
          '@type': 'Organization',
          name: 'Pitchup.com',
          url: 'https://www.pitchup.com/',
        },
      }),
    },
  ],
}))

useHead(headData)

const query = ref('')
const isLoading = ref(false)
const errorMessage = ref<string | null>(null)
const searchInput = ref<HTMLInputElement | null>(null)

// Speech recognition
const {
  isSupported: isSpeechSupported,
  isListening,
  transcript,
  error: speechError,
  toggle: toggleSpeechRecognition,
} = useSpeechRecognition()

// Map locale to speech recognition language code
function getSpeechLang(locale: string): string {
  const langMap: Record<string, string> = {
    'en-gb': 'en-GB',
    'en-us': 'en-US',
    fr: 'fr-FR',
    de: 'de-DE',
    it: 'it-IT',
    es: 'es-ES',
    nl: 'nl-NL',
    pt: 'pt-PT',
    pl: 'pl-PL',
    da: 'da-DK',
    sv: 'sv-SE',
    no: 'nb-NO',
  }
  return langMap[locale] || 'en-GB'
}

// Watch transcript and update query, then auto-submit
watch(transcript, (newTranscript) => {
  if (newTranscript) {
    query.value = newTranscript
    // Auto-submit after voice recognition completes
    void handleSearch()
  }
})

// Watch speech errors and show in error banner
watch(speechError, (newError) => {
  if (newError) {
    errorMessage.value = t(newError)
  }
})

function handleMicClick() {
  toggleSpeechRecognition(getSpeechLang(langCode.value))
}

// Confirmation dialog state
const showConfirmDialog = ref(false)
const pendingUrl = ref('')
const pendingSearchData = ref<AiSearchConfirmData | null>(null)

// Facets and categories for mapping
const facetsStore = useFacetsStore()
const { $gtm } = useNuxtApp()

let widget: any = undefined
// Load facets on mount for mapping
onMounted(async () => {
  await facetsStore.getFacetGroups(langCode.value)

  const feedback = getFeedback()
  widget = feedback?.createWidget({
    triggerLabel: t('Send Feedback'),
    triggerAriaLabel: t('Send feedback'),
    formTitle: t('Send us your feedback'),
    messagePlaceholder: t('How did we do?'),
    submitButtonLabel: t('Send Feedback'),
    successMessageText: t('Thank you for your feedback!'),
  })
})

onUnmounted(() => {
  widget?.removeFromDom()
})

// Helper to map type IDs to accommodation type name
function getAccommodationTypeName(types?: number[]): string | undefined {
  if (!types || types.length === 0) return undefined

  // Check if this is glamping (categories 7 and 13)
  const typeStrings = types.map((typeId) => typeId.toString())
  if (isGlamping(typeStrings)) {
    return t('Glamping')
  }

  try {
    const presenter = getCampsiteCategoriesPresenter(langCode.value)
    // Get names for all types and join them
    const names = types
      .map((id) => presenter.byIdMap[id.toString()]?.name)
      .filter((name): name is string => !!name)
    return names.length > 0 ? names.join(', ') : undefined
  } catch {
    return undefined
  }
}

// Helper to map facet slugs to display names
function getFacetNames(facets?: string[]): string[] {
  if (!facets || facets.length === 0) return []
  return facets
    .map((slug) => facetsStore.facetsBySlug(slug)?.name)
    .filter((name): name is string => !!name)
}

// Helper to replace child ages in URL
function replaceChildAgesInUrl(url: string, ages: number[]): string {
  try {
    const urlObj = new URL(url, window.location.origin)
    urlObj.searchParams.set('child_ages', ages.join(','))
    return urlObj.pathname + urlObj.search
  } catch {
    // Fallback: simple string replacement or append
    const separator = url.includes('?') ? '&' : '?'
    return `${url}${separator}child_ages=${ages.join(',')}`
  }
}

// Confirmation dialog handlers
function handleConfirm(childAges?: number[]) {
  let targetUrl = pendingUrl.value

  // If child ages were provided, replace them in the URL
  if (childAges && childAges.length > 0) {
    targetUrl = replaceChildAgesInUrl(targetUrl, childAges)
  }

  // Track analytics - comprehensive data for analysis
  $gtm.push({
    event: 'ai_search_confirmed',
    query: query.value,
    parsedLocation: pendingSearchData.value?.location,
    parsedArrive: pendingSearchData.value?.arrive,
    parsedDepart: pendingSearchData.value?.depart,
    parsedAdults: pendingSearchData.value?.adults,
    parsedChildren: pendingSearchData.value?.children,
    parsedChildAges:
      childAges?.join(',') || pendingSearchData.value?.childAges?.join(','),
    parsedDogs: pendingSearchData.value?.dogs,
    parsedAccommodationType: pendingSearchData.value?.accommodationType,
    parsedFeatures: pendingSearchData.value?.features?.join(','),
    hadChildAgesPrompt: pendingSearchData.value?.needsChildAges || false,
    childAgesEdited: childAges ? true : false,
    destinationUrl: targetUrl,
  })

  // Close dialog and navigate
  showConfirmDialog.value = false
  pendingSearchData.value = null

  const isExternal = targetUrl.startsWith('http')
  void navigateTo(targetUrl, { external: isExternal })
}

function handleReject() {
  // Track analytics - comprehensive data for analysis of what AI got wrong
  $gtm.push({
    event: 'ai_search_rejected',
    query: query.value,
    parsedLocation: pendingSearchData.value?.location,
    parsedArrive: pendingSearchData.value?.arrive,
    parsedDepart: pendingSearchData.value?.depart,
    parsedAdults: pendingSearchData.value?.adults,
    parsedChildren: pendingSearchData.value?.children,
    parsedChildAges: pendingSearchData.value?.childAges?.join(','),
    parsedDogs: pendingSearchData.value?.dogs,
    parsedAccommodationType: pendingSearchData.value?.accommodationType,
    parsedFeatures: pendingSearchData.value?.features?.join(','),
    hadChildAgesPrompt: pendingSearchData.value?.needsChildAges || false,
  })

  // Close dialog - user can edit query
  showConfirmDialog.value = false
  pendingSearchData.value = null
  pendingUrl.value = ''

  // Focus the input so user can refine
  searchInput.value?.focus()
}

// Shorter placeholders for mobile (fit in input without truncation)
const mobilePlaceholders = computed(() => [
  t('glamping in Cornwall'),
  t('cabin with hot tub'),
  t('camping near beach'),
  t('family camping Devon'),
  t('dog-friendly campsite'),
])

// Longer, more descriptive placeholders for desktop
const desktopPlaceholders = computed(() => [
  t('glamping in Cornwall for 2 adults next weekend'),
  t('dog friendly campsite near the Lake District'),
  t('family caravan park with swimming pool in Devon'),
  t('romantic bell tent for anniversary near London'),
  t('seaside camping with electric hookup in Wales'),
])

// Example queries by locale
const exampleQueriesByLocale: Record<string, string[]> = {
  en: [
    // Romantic / Couples
    'Glamping pod with hot tub for 2 adults in the Lake District, this Friday to Sunday',
    'Romantic cabin with hot tub for 2 in Devon, next weekend',
    // Family Adventures
    'Family glamping for 2 adults and kids aged 5 and 8 in Devon, need a play area',
    'Safari tent for 2 adults and 2 children (ages 4 and 7) with swimming pool',
    'Forest camping in the New Forest for a family of 4, close to wildlife',
    // Dog Owners
    'Dog-friendly glamping pod in the Peak District with good hiking trails, 2 adults and our labrador',
    'Cabin that welcomes dogs with a dog walk nearby in Scotland, 3 nights for 2 adults',
    'Need a last-minute place this weekend for 2 adults and our dog in the Cotswolds',
    // Groups & Friends
    'Camping for a stag do, 6 adults near a pub in Yorkshire this weekend',
    'Bunkhouse for our walking group, 12 adults near Snowdonia in March',
    "Girls' weekend with hot tub and restaurant, 6 of us, no cooking!",
    // Budget Conscious
    'Budget camping for the May bank holiday, 2 adults and 2 kids in England',
    'Affordable glamping in Scotland for 2 adults, 3 nights in June',
    // Luxury / Special Occasions
    'Luxury safari tent for our 10th anniversary, 2 adults with hot tub and restaurant',
    "Husband's 50th birthday - something special with restaurant nearby for 4 adults",
    // Activity Focused
    'Campsite with fishing lake in Norfolk for a dad and son trip (child age 10)',
    'Camping near a golf course in Scotland for 4 adults, Friday to Monday',
    'Campsite with kayaking for 2 adults and 2 teenagers (ages 14 and 16)',
    // Seasonal / Holidays
    "New Year's Eve glamping near Edinburgh for 2 adults with hot tub",
    'Easter camping with play area for 2 adults and kids aged 3 and 6',
    'Summer holiday camping in Dorset for 2 weeks, 2 adults and 3 children',
    // Specific Accommodation
    'Geodesic dome for 2 adults somewhere peaceful and remote this weekend',
    'Gypsy caravan in the countryside for a quirky weekend away, 2 adults',
    // Location Specific
    'Glamping in the Lake District with mountain views, 2 adults next weekend',
    'Camping in the Brecon Beacons for 2 adults, somewhere remote and peaceful',
    // Natural Language / Conversational
    "We want to take the kids (7 and 10) glamping in Devon but I don't want to rough it",
    'First camping trip with our toddler (age 2), somewhere family-friendly with facilities',
    'Planning a surprise anniversary trip - she loves hot tubs and being near the sea',
    'Want to try glamping for the first time, 2 adults, somewhere not too remote',
    'The kids (ages 5, 8 and 10) want to sleep in a treehouse! 2 adults too',
  ],
  fr: [
    // Romantique / Couples
    'On cherche un hébergement insolite avec spa pour 2 en Provence, ce week-end',
    'Cabane perchée romantique avec bain nordique pour 2 en Dordogne, week-end prochain',
    // Famille
    'Glamping pour 2 adultes et nos loulous de 5 et 8 ans en Bretagne, avec aire de jeux',
    'Tente lodge pour 2 adultes et 2 enfants (4 et 7 ans) avec piscine dans le Sud',
    'Camping nature dans les Landes pour une tribu de 4, au calme',
    // Chiens acceptés
    'Hébergement insolite chiens bienvenus dans les Alpes avec randos, 2 adultes et notre toutou',
    'Chalet où les chiens sont acceptés près de la mer, 3 nuits pour 2 adultes',
    // Groupes & Amis
    "Camping pour un EVG, 6 potes près d'un bar en Ardèche ce week-end",
    'Grand gîte pour notre bande de randonneurs, 10 adultes près des Pyrénées en mars',
    'Week-end EVJF avec spa et resto, on est 6 copines',
    // Petit budget
    'Camping pas trop cher pour le pont de mai, 2 adultes et 2 gamins dans le Sud',
    'Glamping sympa en Normandie pour 2, 3 nuits en juin sans se ruiner',
    // Luxe / Occasions spéciales
    'Tente safari de luxe pour nos 10 ans de mariage, 2 adultes avec jacuzzi',
    'Les 50 ans de mon mari - un truc canon avec resto pour 4 adultes',
    // Activités
    'Camping avec étang de pêche en Sologne pour un week-end père-fils (gamin de 10 ans)',
    "Camping près d'un golf en Gironde pour 4 adultes, du vendredi au lundi",
    // Hébergements spécifiques
    'Yourte ou tipi pour 2 adultes dans un coin paumé et tranquille ce week-end',
    'Roulotte tzigane à la campagne pour un week-end décalé, 2 adultes',
    // Conversationnel
    'On veut emmener les enfants (7 et 10 ans) faire du glamping mais pas question de dormir par terre',
    'Premier camping avec notre petit bout (2 ans), un endroit familial avec des vraies toilettes',
    'Je prépare une surprise pour notre anniversaire de mariage - ma femme adore les spas et la mer',
  ],
  it: [
    // Romantico / Coppie
    'Cerco un glamping con idromassaggio per 2 in Toscana, questo fine settimana',
    'Baita romantica con vasca calda per 2 in Trentino, prossimo weekend',
    // Famiglia
    'Glamping per 2 adulti e i bimbi di 5 e 8 anni in Umbria, con parco giochi',
    'Tenda safari per 2 adulti e 2 bambini (4 e 7 anni) con piscina in Puglia',
    'Campeggio nel bosco in Sardegna per 4 persone, immersi nella natura',
    // Cani ammessi
    'Glamping pet-friendly in Alto Adige con sentieri, siamo in 2 col nostro cane',
    'Bungalow dove accettano i cani vicino al mare, 3 notti per 2 adulti',
    // Gruppi & Amici
    'Campeggio per un addio al celibato, 6 amici vicino a locali in Romagna questo weekend',
    'Rifugio per la nostra combriccola di escursionisti, 10 persone vicino alle Dolomiti a marzo',
    'Weekend tra amiche con spa e ristorante, siamo in 6 - addio al nubilato!',
    // Budget
    'Campeggio economico per il ponte del 25 aprile, 2 adulti e 2 bimbi al Sud',
    'Glamping carino in Liguria per 2, 3 notti a giugno senza spendere troppo',
    // Lusso / Occasioni speciali
    'Tenda safari di lusso per i nostri 10 anni insieme, 2 adulti con jacuzzi',
    'I 50 anni di mio marito - qualcosa di speciale con ristorante per 4 adulti',
    // Attività
    'Campeggio con laghetto per pescare in Veneto, weekend padre-figlio (bimbo di 10 anni)',
    'Campeggio vicino a un campo da golf in Piemonte per 4, da venerdì a lunedì',
    // Alloggi specifici
    'Yurta o tenda glamour per 2 in un posto sperduto e tranquillo questo weekend',
    'Roulotte vintage in campagna per un weekend fuori dal comune, 2 adulti',
    // Conversazionale
    'Vogliamo portare i bimbi (7 e 10 anni) a fare glamping ma senza rinunciare ai comfort',
    'Prima vacanza in campeggio col nostro piccolo (2 anni), posto family-friendly con bagni veri',
    'Sto organizzando una sorpresa per il nostro anniversario - lei adora le spa e il mare',
  ],
  de: [
    // Romantisch / Paare
    'Suche Glamping mit Whirlpool für 2 im Schwarzwald, dieses Wochenende',
    'Romantische Blockhütte mit Hottub für 2 in Bayern, nächstes Wochenende',
    // Familie
    'Familien-Glamping für 2 Erwachsene und Kids (5 und 8 Jahre) an der Ostsee, mit Spielplatz',
    'Safari-Zelt für 2 Erwachsene und 2 Kinder (4 und 7 Jahre) mit Pool im Süden',
    'Naturcamping im Allgäu für 4 Personen, schön abgelegen',
    // Hundefreundlich
    'Glamping wo Hunde willkommen sind in den Alpen mit Wanderwegen, wir sind zu zweit mit Hund',
    'Ferienhaus wo Hunde erlaubt sind am Meer, 3 Nächte für 2 Erwachsene',
    // Gruppen & Freunde
    'Camping für nen JGA, 6 Leute in der Nähe von Kneipen am Bodensee dieses Wochenende',
    'Berghütte für unsere Wandertruppe, 10 Leute in Tirol im März',
    'Mädels-Wochenende mit Wellness und Restaurant, wir sind 6 Frauen',
    // Budget
    'Günstiges Camping über Pfingsten, 2 Erwachsene und 2 Kids in Süddeutschland',
    'Bezahlbares Glamping an der Nordsee für 2, 3 Nächte im Juni',
    // Luxus / Besondere Anlässe
    'Luxus-Safari-Zelt für unseren 10. Hochzeitstag, zu zweit mit Jacuzzi',
    '50. Geburtstag meiner Frau - was Besonderes mit Restaurant für 4 Erwachsene',
    // Aktivitäten
    'Campingplatz mit Angelsee in Brandenburg für Vater-Sohn-Trip (Sohn ist 10)',
    'Camping in der Nähe von nem Golfplatz in Schleswig-Holstein für 4, Freitag bis Montag',
    // Besondere Unterkünfte
    'Jurte oder Tipi für 2 irgendwo ruhig und abgelegen dieses Wochenende',
    'Bauwagen oder Zirkuswagen auf dem Land für ein ausgefallenes Wochenende, 2 Erwachsene',
    // Umgangssprachlich
    'Wir wollen mit den Kids (7 und 10) glampen, aber bitte mit Komfort',
    'Erster Campingurlaub mit unserem Kleinen (2 Jahre), familienfreundlich mit richtigen Toiletten',
    'Plane ne Überraschung zum Jahrestag - meine Frau steht total auf Wellness und Meer',
  ],
  'en-us': [
    // Romantic / Couples
    'Glamping with hot tub for 2 adults near Big Sur, this weekend',
    'Romantic cabin with jacuzzi for 2 in the Smoky Mountains, next weekend',
    // Family Adventures
    'Family glamping for 2 adults and kids aged 5 and 8 near Yellowstone, need kid-friendly activities',
    'Cabin for 2 adults and 2 children (ages 4 and 7) with pool near Orlando',
    'Camping in the Adirondacks for a family of 4, close to hiking trails',
    // Dog Owners
    'Dog-friendly glamping near Yosemite with hiking trails, 2 adults and our golden retriever',
    'Cabin that welcomes dogs near Lake Tahoe, 3 nights for 2 adults',
    'Pet-friendly camping this weekend for 2 adults and our dog in Colorado',
    // Groups & Friends
    'Camping for a bachelor party, 6 adults near Austin this weekend',
    'Cabin for our hiking group, 10 adults near the Grand Canyon in March',
    "Girls' weekend with hot tub and restaurant, 6 of us in Napa Valley",
    // Budget Conscious
    'Budget camping for Memorial Day weekend, 2 adults and 2 kids in California',
    'Affordable glamping in Vermont for 2 adults, 3 nights in June',
    // Luxury / Special Occasions
    'Luxury safari tent for our 10th anniversary, 2 adults with hot tub near Sedona',
    "Wife's 40th birthday - something special with restaurant for 4 adults in wine country",
    // Activity Focused
    'Campsite with fishing in Montana for a dad and son trip (child age 10)',
    'Camping near a golf course in Scottsdale for 4 adults, Friday to Monday',
    'Campsite with kayaking for 2 adults and 2 teenagers (ages 14 and 16) in Maine',
    // Seasonal / Holidays
    'Fourth of July glamping near the beach for 2 adults with ocean view',
    'Thanksgiving camping with activities for 2 adults and kids aged 3 and 6',
    'Summer vacation camping in Michigan for 2 weeks, 2 adults and 3 children',
    // Specific Accommodation
    'Treehouse for 2 adults somewhere peaceful and remote this weekend',
    'Airstream rental in the desert for a unique weekend, 2 adults',
    // Natural Language / Conversational
    'We want to take the kids (7 and 10) glamping in California but with real beds',
    'First camping trip with our toddler (age 2), somewhere family-friendly with bathrooms',
    'Planning a surprise anniversary trip - she loves hot tubs and mountain views',
  ],
}

// Get examples for current locale (fallback to English)
function getExamplesForLocale(locale: string): string[] {
  // Check for exact locale match first (e.g., en-us), then base language (e.g., en)
  if (exampleQueriesByLocale[locale]) {
    return exampleQueriesByLocale[locale]
  }
  const lang = locale.split('-')[0]
  return exampleQueriesByLocale[lang] || exampleQueriesByLocale.en
}

// Number of examples to show at once
const VISIBLE_EXAMPLES_COUNT = 8
const EXAMPLE_ROTATION_INTERVAL = 8000 // 8 seconds

// Simple seeded random number generator (for SSR/client consistency)
function seededRandom(seed: number): () => number {
  return () => {
    seed = (seed * 1103515245 + 12345) & 0x7fffffff
    return seed / 0x7fffffff
  }
}

// Shuffle array using Fisher-Yates with seeded random
function shuffleArraySeeded<T>(array: T[], seed: number): T[] {
  const shuffled = [...array]
  const random = seededRandom(seed)
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1))
    const temp = shuffled[i]
    shuffled[i] = shuffled[j] as T
    shuffled[j] = temp as T
  }
  return shuffled
}

// Shuffle array using Math.random (for client-side rotation)
function shuffleArray<T>(array: T[]): T[] {
  const shuffled = [...array]
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    const temp = shuffled[i]
    shuffled[i] = shuffled[j] as T
    shuffled[j] = temp as T
  }
  return shuffled
}

// SSR-safe random seed - generated once on server, hydrated on client
const examplesSeed = useState<number>('ai-examples-seed', () =>
  Math.floor(Math.random() * 1000000),
)

// Get random subset of examples for current locale (using seed for SSR consistency)
function getSeededExamples(count: number): string[] {
  const examples = getExamplesForLocale(langCode.value)
  return shuffleArraySeeded(examples, examplesSeed.value).slice(0, count)
}

// Get random subset of examples for current locale (true random for rotation)
function getRandomExamples(count: number): string[] {
  const examples = getExamplesForLocale(langCode.value)
  return shuffleArray(examples).slice(0, count)
}

// Initialize with seeded random examples (consistent between SSR and client hydration)
const displayExamples = computed(() =>
  getSeededExamples(VISIBLE_EXAMPLES_COUNT),
)
// Track if we've randomized (for client-side rotation)
const randomizedExamples = ref<string[] | null>(null)
const activeExamples = computed(
  () => randomizedExamples.value || displayExamples.value,
)
const isExamplesTransitioning = ref(false)

const currentPlaceholderIndex = ref(0)
const activePlaceholders = computed(() =>
  $isDesktop ? desktopPlaceholders.value : mobilePlaceholders.value,
)
const currentPlaceholder = computed(
  () => activePlaceholders.value[currentPlaceholderIndex.value],
)

let placeholderInterval: ReturnType<typeof setInterval> | null = null
let examplesInterval: ReturnType<typeof setInterval> | null = null

function rotateExamples() {
  isExamplesTransitioning.value = true
  // Wait for fade out, then swap examples
  setTimeout(() => {
    randomizedExamples.value = getRandomExamples(VISIBLE_EXAMPLES_COUNT)
    isExamplesTransitioning.value = false
  }, 300) // Match CSS transition duration
}

onMounted(() => {
  // Don't randomize immediately - let SSR examples stay to avoid flicker
  // The interval will randomize after EXAMPLE_ROTATION_INTERVAL (8 seconds)

  // Rotate placeholder text
  placeholderInterval = setInterval(() => {
    currentPlaceholderIndex.value =
      (currentPlaceholderIndex.value + 1) % activePlaceholders.value.length
  }, 4000)

  // Rotate example cards
  examplesInterval = setInterval(rotateExamples, EXAMPLE_ROTATION_INTERVAL)
})

onUnmounted(() => {
  if (placeholderInterval) {
    clearInterval(placeholderInterval)
  }
  if (examplesInterval) {
    clearInterval(examplesInterval)
  }
})

function clearInput() {
  query.value = ''
  errorMessage.value = null
  searchInput.value?.focus()
}

function searchWithExample(example: string) {
  query.value = example
  void handleSearch()
}

interface AiSearchDebug {
  query: string
  lang_locale: string
  types?: number[]
  location_query?: string
  location_source?: string
  arrive?: string
  depart?: string
  adults?: number
  children?: number
  child_ages?: number[]
  needs_child_ages?: boolean
  dogs?: number
  facets?: string[]
  sort?: string
}

async function handleSearch() {
  if (isLoading.value) return
  const trimmedQuery = query.value.trim()
  if (!trimmedQuery) return

  try {
    setContext('AI Search', { query: trimmedQuery })
  } catch {
    console.error('Failed to set Sentry context for AI Search')
  }

  isLoading.value = true
  errorMessage.value = null

  try {
    const response = await $fetch<{
      success: boolean
      url?: string
      error?: string
      needs_child_ages?: boolean
      debug?: AiSearchDebug
    }>('/api/ai/search', {
      method: 'POST',
      body: {
        query: trimmedQuery,
        lang_locale: langCode.value,
      },
    })

    if (import.meta.dev && response.debug) {
      // eslint-disable-next-line no-console
      console.log('[AI Search] Debug:', response.debug)
    }

    if (response.success && response.url) {
      // Convert absolute pitchup URLs to relative paths for SPA navigation
      let targetUrl = response.url
      if (targetUrl.startsWith('http')) {
        try {
          const parsed = new URL(targetUrl)
          // If it's a pitchup domain, use relative path for SPA nav
          if (
            /^(www\.)?(local\.|staging)?pitchup\.com$/.test(parsed.hostname)
          ) {
            targetUrl = parsed.pathname + parsed.search
          }
        } catch {
          // Invalid URL, use as-is
        }
      }

      // Prepare confirmation dialog data
      const debug = response.debug

      // eslint-disable-next-line no-console
      console.log('[AI Search] Response debug:', debug)
      // eslint-disable-next-line no-console
      console.log('[AI Search] needs_child_ages:', response.needs_child_ages)
      // eslint-disable-next-line no-console
      console.log('[AI Search] URL:', targetUrl)

      // Extract child ages from URL as fallback if not in debug
      let childAges = debug?.child_ages
      if (!childAges && targetUrl.includes('child_ages=')) {
        try {
          const urlObj = new URL(targetUrl, window.location.origin)
          const agesParam = urlObj.searchParams.get('child_ages')
          if (agesParam) {
            childAges = agesParam
              .split(',')
              .map((a) => parseInt(a, 10))
              .filter((a) => !isNaN(a))
            // eslint-disable-next-line no-console
            console.log('[AI Search] Extracted child_ages from URL:', childAges)
          }
        } catch {
          // ignore
        }
      }

      // Cap values to match dropdown limits
      const MAX_ADULTS = 30
      const MAX_CHILDREN = 30
      const MAX_DOGS = 30

      const cappedAdults = Math.min(debug?.adults ?? 0, MAX_ADULTS)
      const cappedChildren = Math.min(debug?.children ?? 0, MAX_CHILDREN)
      const cappedDogs = Math.min(debug?.dogs ?? 0, MAX_DOGS)

      // Update URL with capped values if any were capped
      if (
        cappedAdults !== debug?.adults ||
        cappedChildren !== debug?.children ||
        cappedDogs !== debug?.dogs
      ) {
        try {
          const urlObj = new URL(targetUrl, window.location.origin)
          if (debug?.adults !== undefined && cappedAdults !== debug.adults) {
            urlObj.searchParams.set('adults', cappedAdults.toString())
          }
          if (
            debug?.children !== undefined &&
            cappedChildren !== debug.children
          ) {
            urlObj.searchParams.set('children', cappedChildren.toString())
          }
          if (debug?.dogs !== undefined && cappedDogs !== debug.dogs) {
            urlObj.searchParams.set('dogs', cappedDogs.toString())
          }
          targetUrl = urlObj.pathname + urlObj.search
        } catch {
          // ignore URL parsing errors
        }
      }

      // Also cap childAges array to match capped children count
      const cappedChildAges = response.needs_child_ages
        ? []
        : childAges?.slice(0, cappedChildren)

      pendingSearchData.value = {
        location: debug?.location_query,
        arrive: debug?.arrive,
        depart: debug?.depart,
        adults: cappedAdults,
        children: cappedChildren,
        // If needs ages, start blank; otherwise use backend ages (capped)
        childAges: cappedChildAges,
        needsChildAges: response.needs_child_ages,
        dogs: cappedDogs,
        accommodationType: getAccommodationTypeName(debug?.types),
        features: getFacetNames(debug?.facets),
      }
      pendingUrl.value = targetUrl

      // eslint-disable-next-line no-console
      console.log('[AI Search] Pending search data:', pendingSearchData.value)

      // Only show dialog if child ages need to be entered
      if (pendingSearchData.value?.needsChildAges) {
        showConfirmDialog.value = true
      } else {
        // Track analytics for direct navigation
        $gtm.push({
          event: 'ai_search_confirmed',
          query: query.value,
          parsedLocation: pendingSearchData.value?.location,
          parsedArrive: pendingSearchData.value?.arrive,
          parsedDepart: pendingSearchData.value?.depart,
          parsedAdults: pendingSearchData.value?.adults,
          parsedChildren: pendingSearchData.value?.children,
          parsedChildAges: pendingSearchData.value?.childAges?.join(','),
          parsedDogs: pendingSearchData.value?.dogs,
          parsedAccommodationType: pendingSearchData.value?.accommodationType,
          parsedFeatures: pendingSearchData.value?.features?.join(','),
          hadChildAgesPrompt: false,
          childAgesEdited: false,
          destinationUrl: targetUrl,
        })

        // Navigate directly
        const isExternal = targetUrl.startsWith('http')
        void navigateTo(targetUrl, { external: isExternal })
      }
    } else {
      errorMessage.value =
        response.error || t('Something went wrong. Please try again.')
    }
  } catch (error: unknown) {
    console.error('[AI Search] Client error:', error)

    if (error && typeof error === 'object' && 'data' in error) {
      const errorData = (error as { data?: { error?: string } }).data
      if (errorData?.error) {
        errorMessage.value = errorData.error
        return
      }
    }

    errorMessage.value = t(
      'Unable to connect. Please check your connection and try again.',
    )
  } finally {
    isLoading.value = false
  }
}
</script>

<style scoped>
.ai-search-page {
  min-height: 100dvh;
}

/* Desktop container - match homepage */
.desktop.ai-search-page {
  max-width: var(--page-width-max);
  margin: 0 auto;
}

/* ===== HERO ===== */
.ai-hero {
  position: relative;
  padding: 3em 1.4em 3.5em;
  background: linear-gradient(145deg, #1a7a32 0%, #166b2c 35%, #0d5f6a 100%);
  overflow: hidden;
}

.desktop .ai-hero {
  padding: 5em 5em 6em;
}

@media (min-width: 1606px) {
  .desktop .ai-hero {
    border-radius: 1em 1em 0 0;
  }
}

.hero-pattern {
  position: absolute;
  inset: 0;
  opacity: 1;
  pointer-events: none;
}

.topo-lines {
  width: 100%;
  height: 100%;
}

.hero-content {
  position: relative;
  z-index: 2;
  text-align: center;
  max-width: 100%;
  margin: 0 auto;
}

.desktop .hero-content {
  max-width: 900px;
}

/* Title */
.ai-title {
  font-size: 1.9em;
  font-weight: 700;
  color: #fff;
  line-height: 1.15;
  margin: 0 0 0.5em;
}

.desktop .ai-title {
  font-size: 3em;
}

.title-highlight {
  color: #fcd34d;
}

.ai-subtitle {
  color: rgba(255, 255, 255, 0.85);
  font-size: 1em;
  line-height: 1.5;
  margin: 0 0 1.75em;
  max-width: 480px;
  margin-left: auto;
  margin-right: auto;
}

.desktop .ai-subtitle {
  font-size: 1.15em;
}

/* ===== SEARCH CARD ===== */
.search-card {
  background: #fff;
  border-radius: 0.65em;
  padding: 1em;
  box-shadow:
    0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 20px 40px -8px rgba(0, 0, 0, 0.2);
}

.desktop .search-card {
  padding: 1.5em 2em;
  border-radius: 1.25em;
  max-width: 800px;
  margin: 0 auto;
}

.search-form {
  display: flex;
  flex-direction: column;
  gap: 0.75em;
}

.desktop .search-form {
  flex-direction: row;
  gap: 0.75em;
}

.input-wrapper {
  position: relative;
  flex: 1;
}

.input-icon {
  position: absolute;
  left: 0.4em;
  top: 50%;
  transform: translateY(-50%);
  width: 1.2em;
  height: 1.2em;
  color: #9ca3af;
  pointer-events: none;
}

.desktop .input-icon {
  width: 1.4em;
  height: 1.4em;
  left: 1em;
}

.input-icon svg {
  width: 100%;
  height: 100%;
}

.ai-search-input {
  width: 100%;
  padding: 0.5em 2.5em 0.5em 1.8em;
  font-size: 1em;
  background: #fff;
  transition:
    border-color 0.15s,
    box-shadow 0.15s;
  border-radius: 4em;
  border: 2px solid #6496c8;
  margin: 0;
}

.desktop .ai-search-input {
  padding: 1em 2.5em 1em 2.7em;
  font-size: 1.1em;
}

.ai-search-input:hover {
  border-color: #d1d5db;
}

.ai-search-input:focus {
  outline: none;
  border-color: #0072bc;
  box-shadow: 0 0 0 3px rgba(0, 114, 188, 0.15);
}

.ai-search-input::placeholder {
  color: #9ca3af;
  font-size: 0.9em;
}

.desktop .ai-search-input::placeholder {
  font-size: 1em;
}

.clear-btn {
  position: absolute;
  right: 0.75em;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  padding: 0;
  width: 1.4em;
  height: 1.4em;
  color: #9ca3af;
  cursor: pointer;
  transition: color 0.15s;
  display: flex;
  box-shadow: none;
}

.clear-btn:hover {
  color: #6b7280;
}

.clear-btn svg {
  width: 100%;
  height: 100%;
}

.mic-btn {
  position: absolute;
  top: 50%;
  right: 10px;
  transform: translateY(-50%);
  background-color: rgba(100, 150, 200, 0.12);
  border: none;
  border-radius: 50%;
  padding: 6px;
  box-shadow: none;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.mic-btn:hover:not(:disabled) {
  background-color: rgba(100, 150, 200, 0.25);
  transform: translateY(-50%) scale(1.05);
}

.mic-btn:active {
  transform: translateY(-50%) scale(0.95);
}

.mic-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.mic-btn.is-listening {
  background-color: rgba(245, 158, 11, 0.2);
}

.mic-btn.is-listening .mic-icon {
  color: var(--colour-orange, #f59e0b);
}

.mic-icon {
  width: 20px;
  height: 20px;
  color: #6496c8;
}

.mic-pulse {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background-color: currentColor;
  opacity: 0.2;
  animation: mic-pulse 1.5s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

@keyframes mic-pulse {
  0% {
    transform: translate(-50%, -50%) scale(0.8);
    opacity: 0.5;
  }
  100% {
    transform: translate(-50%, -50%) scale(2.2);
    opacity: 0;
  }
}

.ai-search-input.with-action-btn {
  padding-right: 48px;
}

.btn-icon {
  width: 1em;
  height: 1em;
  transition: transform 0.2s;
}

.btn-spinner {
  position: absolute;
}

/* ===== ERROR ===== */
.error-banner {
  display: flex;
  align-items: center;
  gap: 0.75em;
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #dc2626;
  padding: 0.75em 1em;
  border-radius: 0.5em;
  margin-top: 0.75em;
}

.error-icon {
  width: 1.25em;
  height: 1.25em;
  flex-shrink: 0;
}

.error-banner p {
  flex: 1;
  margin: 0;
  font-size: 0.9em;
}

.dismiss-btn {
  background: none;
  border: none;
  padding: 0;
  width: 1.25em;
  height: 1.25em;
  color: #dc2626;
  cursor: pointer;
  opacity: 0.6;
  box-shadow: none;
}

.dismiss-btn:hover {
  opacity: 1;
}

.dismiss-btn svg {
  width: 100%;
  height: 100%;
}

.error-slide-enter-active,
.error-slide-leave-active {
  transition: all 0.25s ease;
}

.error-slide-enter-from,
.error-slide-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

/* ===== EXAMPLES ===== */
.examples-section {
  padding: 1.25em 1.4em 1.5em;
  box-sizing: border-box;
  width: 100%;
  background: #e9edf1;
}

.desktop .examples-section {
  padding: 2.5em 5em 4em;
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5em;
  margin-bottom: 1.25em;
}

.header-icon {
  width: 1.25em;
  height: 1.25em;
  color: #f59e0b;
}

.desktop .header-icon {
  width: 1.5em;
  height: 1.5em;
}

.section-header h2 {
  font-size: 1.2em;
  font-weight: 600;
  color: var(--colour-wcag-blue, #374151);
  margin: 0;
}

.desktop .section-header h2 {
  font-size: 1.75em;
}

.examples-grid {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 0.6em;
  width: 100%;
  justify-content: flex-start;
  align-items: flex-start;
  margin: 0;
  padding: 0;
  transition: opacity 0.3s ease-in-out;
}

.examples-grid.is-transitioning {
  opacity: 0;
}

.desktop .examples-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1em;
  max-width: 1200px;
  margin: 0 auto;
}

.example-card {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75em;
  width: 100%;
  flex-basis: 100%;
  min-width: 0;
  margin: 0;
  box-sizing: border-box;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 0.75em;
  padding: 1em 1.1em;
  font-size: 0.95em;
  color: #374151;
  cursor: pointer;
  text-align: left;
  transition:
    border-color 0.15s,
    background 0.15s,
    box-shadow 0.15s,
    transform 0.15s;
  box-shadow: var(--card-box-shadow);
  animation: card-in 0.4s ease backwards;
  animation-delay: var(--delay);
  align-self: stretch;
}

.example-text {
  flex: 1;
  min-width: 0;
  line-height: 1.4;
  word-wrap: break-word;
}

@keyframes card-in {
  from {
    opacity: 0;
    transform: translateY(8px);
  }
}

.desktop .example-card {
  padding: 1em 1.25em;
  font-size: 1em;
}

.example-card:hover:not(:disabled) {
  border-color: #0072bc;
  background: #f0f9ff;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 114, 188, 0.1);
}

.example-card:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.example-arrow {
  flex-shrink: 0;
  width: 1.5em;
  height: 1.5em;
  margin-top: 0.1em;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f3f4f6;
  border-radius: 50%;
  color: #6b7280;
  font-size: 0.85em;
  font-weight: 600;
  transition:
    transform 0.15s,
    color 0.15s,
    background 0.15s;
}

.example-card:hover .example-arrow {
  color: #fff;
  background: #0072bc;
  transform: translateX(2px);
}

/* ===== HOW IT WORKS ===== */
.how-section {
  padding: 2.5em 1.4em 3em;
}

.desktop .how-section {
  padding: 4em 5em 5em;
}

.how-section h2 {
  font-size: 1.2em;
  font-weight: 700;
  color: var(--colour-wcag-blue, #1f2937);
  text-align: center;
  margin: 0 0 2em;
}

.desktop .how-section h2 {
  font-size: 2em;
  margin-bottom: 2.5em;
}

.steps-flow {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.5em;
}

.desktop .steps-flow {
  flex-direction: row;
  justify-content: center;
  gap: 1.5em;
  max-width: 1000px;
  margin: 0 auto;
}

.step-card {
  text-align: center;
  max-width: 200px;
}

.desktop .step-card {
  max-width: 240px;
}

.step-visual {
  width: 4em;
  height: 4em;
  border-radius: 1em;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 0.75em;
  position: relative;
}

.step-visual svg {
  width: 1.75em;
  height: 1.75em;
}

.step-visual.write {
  background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
  color: #1d4ed8;
}

.step-visual.think {
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
  color: #b45309;
}

.step-visual.success {
  background: linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%);
  color: #059669;
}

.think-dots {
  position: absolute;
  bottom: -0.5em;
  display: flex;
  gap: 0.25em;
}

.think-dots span {
  width: 6px;
  height: 6px;
  background: #f59e0b;
  border-radius: 50%;
  animation: think-bounce 1.4s ease-in-out infinite;
}

.think-dots span:nth-child(2) {
  animation-delay: 0.2s;
}

.think-dots span:nth-child(3) {
  animation-delay: 0.4s;
}

@keyframes think-bounce {
  0%,
  80%,
  100% {
    transform: scale(0.8);
    opacity: 0.5;
  }
  40% {
    transform: scale(1);
    opacity: 1;
  }
}

.step-card h3 {
  font-size: 1em;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 0.35em;
}

.desktop .step-card h3 {
  font-size: 1.2em;
}

.step-card p {
  font-size: 0.85em;
  color: #6b7280;
  margin: 0;
  line-height: 1.4;
}

.desktop .step-card p {
  font-size: 1em;
}

.step-connector {
  display: none;
  color: #d1d5db;
}

.desktop .step-connector {
  display: flex;
  align-items: center;
  padding: 0 0.5em;
}

.step-connector svg {
  width: 1.5em;
  height: 1.5em;
}

/* ===== MOBILE LANDSCAPE ===== */
@media screen and (min-width: 480px) and (max-width: 998px) and (orientation: landscape) {
  .ai-hero {
    padding: 2.5em 2em 3em;
  }

  .hero-content {
    max-width: 700px;
  }

  .search-form {
    flex-direction: row;
    gap: 0.75em;
  }

  .examples-section {
    padding: 2em 2em;
  }

  .examples-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 0.75em;
  }

  .how-section {
    padding: 2.5em 2em 3em;
  }

  .steps-flow {
    flex-direction: row;
    gap: 1em;
  }

  .step-connector {
    display: flex;
    align-items: center;
    padding: 0 0.25em;
  }
}

/* ===== TABLET PORTRAIT ===== */
@media screen and (min-width: 600px) and (max-width: 998px) and (orientation: portrait) {
  .ai-hero {
    padding: 3.5em 2.5em 4em;
  }

  .hero-content {
    max-width: 600px;
  }

  .search-form {
    flex-direction: row;
    gap: 0.75em;
  }

  .examples-section {
    padding: 2.5em 2.5em;
  }

  .examples-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 0.75em;
  }

  .how-section {
    padding: 3em 2.5em;
  }

  .how-section h2 {
    font-size: 1.5em;
  }
}

/* ===== SMALL MOBILE ===== */
@media screen and (max-width: 359px) {
  .ai-hero {
    padding: 2em 1em 2.5em;
  }

  .ai-title {
    font-size: 1.5em;
  }

  .ai-subtitle {
    font-size: 0.9em;
  }

  .ai-search-input {
    font-size: 0.95em;
    padding: 0.8em 2.2em 0.8em 2.5em;
  }

  .example-card {
    padding: 0.85em 1em;
    font-size: 0.9em;
  }

  .example-arrow {
    width: 1.3em;
    height: 1.3em;
  }
}

/* ===== TIPS SECTION ===== */
.tips-section {
  padding: 1em 1.4em 1.25em;
  background: #f4f6f8;
  display: flex;
  flex-direction: column;
}

.desktop .tips-section {
  padding: 2.5em 5em;
}

.tips-header {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.4em;
  margin-bottom: 0.75em;
}

.tips-icon {
  width: 1.1em;
  height: 1.1em;
  color: #16a34a;
}

.tips-label {
  font-size: 0.9em;
  font-weight: 600;
  color: #166534;
}

.tips-section .tips-grid {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.5em 1em;
}

.desktop .tips-section .tips-grid {
  gap: 1.25em 3.5em;
}

.desktop .tip-item {
  gap: 0.5em;
}

.tip-item {
  display: flex;
  align-items: center;
  gap: 0.35em;
}

.tip-emoji {
  width: 1.5em;
  img {
    filter: grayscale(1) brightness(0.25) opacity(0.55);
  }
}

.tip-text {
  font-size: var(--body);
  color: #374151;
}

.tip-text strong {
  color: var(--colour-dark-blue);
}

.desktop .tip-text {
  font-size: inherit;
}

@media screen and (max-width: 768px) {
  .tips-section .tips-grid {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.4em;
    margin: 0 auto;
  }
}
</style>

<style>
#sentry-feedback {
  font-size: 1rem;
}
</style>
